import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { 
  Save, Search, Box, Plus, ShieldAlert, Activity, FileText, GitBranch, List, Filter, Download, Maximize2, Calendar, 
  History, MapPin, Trash2, Camera, ChevronRight, Globe, DollarSign, Upload, RefreshCw, ArrowLeft, ChevronLeft, Paperclip, Minus, Type, Briefcase, Zap   
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import StatusModal  from './StatusModal';
import StatusHistoryModal  from './StatusHistoryModal';
import UniversalLookup from './UniversalLookup';
import ClassificationTree from './ClassificationTree';
import EnterLocMeterReadings from './EnterLocMeterReadings';
import MeterHistoryModal from './LocMeterHistoryModal';
import IncidentModal from './IncidentModal';
import IncidentHistoryModal from './IncidentHistoryModal';
import AttachmentsModal from './AttachmentsModal';
import SmartPrintButton from './PrintButton'; 
import { useFontSize } from './FontSizeContext';
import api from './axiosConfig'; 

  // =================================================================
  // 1. BIM Viewer (النسخة الخارقة المُجهزة بالديجيتال توين)
  // =================================================================
  const BIMViewer = ({ urn, activeGuid, locationData, t }) => {
    const viewerContainer = useRef(null);
    const viewer = useRef(null);
    const isInitializing = useRef(false);
    const bubbleDataRef = useRef(null);
    const [isTreeReady, setIsTreeReady] = useState(false);

    useEffect(() => {
      if (!urn || viewer.current || isInitializing.current) return;
      isInitializing.current = true;
      const Autodesk = window.Autodesk;

      const options = {
        env: 'AutodeskProduction2',
        api: 'streamingV2',
        getAccessToken: async (callback) => {
          try {
            const res = await axios.get('/api/bim/token');
            callback(res.data.access_token, res.data.expires_in);
          } catch (error) { console.error(error); }
        }
      };

      Autodesk.Viewing.Initializer(options, () => {
        viewer.current = new Autodesk.Viewing.GuiViewer3D(viewerContainer.current);
        viewer.current.start();
        const documentId = 'urn:' + urn;
        Autodesk.Viewing.Document.load(documentId, (doc) => {
          const viewables = doc.getRoot().getDefaultGeometry();
          viewer.current.addEventListener(Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT, () => setIsTreeReady(true));
          viewer.current.loadDocumentNode(doc, viewables);
        });
      });

      return () => {
        if (viewer.current) { viewer.current.finish(); viewer.current = null; isInitializing.current = false; }
      };
    }, [urn]);

    // الزووم والتركيز الذكي
    useEffect(() => {
      if (!isTreeReady || !viewer.current || !viewer.current.model || !activeGuid || !window.THREE) return;
      const v = viewer.current;
      const handleFocus = (dbId) => {
        v.fitToView([dbId]); v.select([dbId]);    
        setTimeout(() => v.clearSelection(), 2000); 
      };
      v.model.getExternalIdMapping((mapping) => {
        const dbId = mapping[activeGuid];
        if (dbId) handleFocus(dbId);
        else v.search(activeGuid, (dbIds) => { if (dbIds?.length > 0) handleFocus(dbIds[0]); }, (err) => console.error(err), ['externalId']);
      });
    }, [activeGuid, isTreeReady]);

    // رسم البابل (بدون Lag)
    const renderBubble = useCallback(() => {
      if (!bubbleDataRef.current || !viewer.current ) return;
      const { data, center3D } = bubbleDataRef.current;
      const screenPoint = viewer.current.worldToClient(center3D);
      if (!screenPoint) return;

      let bubble = document.getElementById(`bubble-${data.guid}`);
      if (!bubble) {
          bubble = document.createElement('div');
          bubble.id = `bubble-${data.guid}`;
          viewer.current.container.appendChild(bubble);
      }

      bubble.style.position = 'absolute';
      bubble.style.zIndex = '999999'; 
      bubble.style.left = `${screenPoint.x}px`;
      bubble.style.top = `${screenPoint.y - 20}px`; 
      bubble.style.transform = 'translate(-50%, -100%)'; 
      bubble.style.pointerEvents = 'none'; 
      bubble.style.transition = 'none'; // لالتصاق البابل
      
      const borderColor = data.status === 'BROKEN' ? '#dc2626' : '#22c55e'; 
      bubble.style.borderLeft = `5px solid ${borderColor}`;
      bubble.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
      bubble.style.backdropFilter = 'blur(4px)';
      bubble.style.padding = '12px';
      bubble.style.borderRadius = '8px';
      bubble.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.2)';
      bubble.style.minWidth = '160px';
      bubble.style.fontFamily = 'sans-serif';
      bubble.style.display = screenPoint.z > 1 ? 'none' : 'block';

      const metersHtml = (data.meters || []).map(m => `
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 2px;" dir="ltr">
              <span style="font-size: 10px; color: #6b7280; font-weight: bold;">${m.meter_name || 'Meter'}:</span>
              <span style="font-size: 11px; font-weight: 900; color: ${(m.last_reading || 0) > 50 ? '#dc2626' : '#16a34a'}">
                  ${m.last_reading || 0} <span style="font-size: 8px; color: #9ca3af;">${m.unit_of_measure || ''}</span>
              </span>
          </div>
      `).join('');

      bubble.innerHTML = `
          <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; margin-bottom: 6px;" dir="ltr">
              <span style="font-size: 12px; font-weight: 900; color: #1e293b; letter-spacing: 0.5px;">${data.location_id}</span>
              <span style="font-size: 9px; padding: 2px 6px; background-color: #dbeafe; color: #1e40af; border-radius: 4px; font-weight: bold; animation: pulse 2s infinite;">LIVE</span>
          </div>
          ${metersHtml}
      `;
    }, []);

    // الحقن التلقائي
    useEffect(() => {
      if (!isTreeReady || !viewer.current || !activeGuid || !window.THREE || !locationData) return;
      const autoBubbleTimer = setTimeout(() => {
          const initialPayload = {
              guid: activeGuid,
              location_id: locationData.location_id || '---',
              status: locationData.status || 'OPERATING',
              meters: locationData.meters || [],
          };
          window.dispatchEvent(new CustomEvent('showAssetBubble', { detail: initialPayload }));
      }, 1500); 
      return () => clearTimeout(autoBubbleTimer);
    }, [isTreeReady, activeGuid, locationData]);

    // مراقب الكاميرا
    useEffect(() => {
      if (!isTreeReady || !viewer.current) return;
      const onCam = () => { if (bubbleDataRef.current) renderBubble(); };
      viewer.current.addEventListener(window.Autodesk.Viewing.CAMERA_CHANGE_EVENT, onCam);
      return () => viewer.current?.removeEventListener(window.Autodesk.Viewing.CAMERA_CHANGE_EVENT, onCam);
    }, [isTreeReady, renderBubble]);

    // الاستماع للسوكيت
    useEffect(() => {
      if (!isTreeReady || !viewer.current || !window.THREE) return;
      const handleShowBubble = (e) => {
          const newData = e.detail;
          const v = viewer.current;
          v.model.getExternalIdMapping((mapping) => {
              const dbId = mapping[newData.guid];
              if (!dbId) return;
              const tree = v.model.getInstanceTree();
              const fragList = v.model.getFragmentList();
              const bbox = new window.THREE.Box3();
              tree.enumNodeFragments(dbId, (fragId) => {
                  const fragBox = new window.THREE.Box3();
                  fragList.getWorldBounds(fragId, fragBox);
                  bbox.union(fragBox);
              }, true);
              const center3D = new window.THREE.Vector3((bbox.min.x + bbox.max.x) / 2, (bbox.min.y + bbox.max.y) / 2, bbox.max.z);
              bubbleDataRef.current = { data: { ...(bubbleDataRef.current?.data || {}), ...newData }, center3D };
              renderBubble(); 
          });
      };
      window.addEventListener('showAssetBubble', handleShowBubble);
      return () => { window.removeEventListener('showAssetBubble', handleShowBubble); };
    }, [isTreeReady, renderBubble]);

    if (!urn) return (
      <div className="flex flex-col items-center justify-center h-full text-slate-500">
        <ShieldAlert size={48} className="mb-4 opacity-20" />
        <p className="font-bold uppercase tracking-widest">{t('locationDetails.noBimModel', 'NO 3D MODEL LINKED')}</p>
      </div>
    );

    return (
        <div className="w-full h-full relative">
            {!isTreeReady && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-slate-900/80 backdrop-blur-sm text-blue-400">
                    <div className="w-12 h-12 border-4 border-blue-400 border-t-transparent rounded-full animate-spin mb-4"></div>
                    <span className="text-[10px] font-black uppercase tracking-widest animate-pulse">Building Digital Twin...</span>
                </div>
            )}
            <div ref={viewerContainer} className="w-full h-full rounded-xl overflow-hidden" />
        </div>
    );
  };

  // =================================================================
  // 2. Map View (النسخة الخرافية اللي بتعرض الموقع والأصول التابعة ليه)
  // =================================================================
  const MapView = ({ lat, lng, locationId, bim_id, status, childAssets, onLocationChange, onOpen3D, onOpenAsset3D, t, isRTL }) => {
    const markerRef = useRef(null);
    const center = [lat || 30.0444, lng || 31.2357];
  
    const eventHandlers = useMemo(() => ({
        dragend() {
          const marker = markerRef.current;
          if (marker != null) {
            const newPos = marker.getLatLng();
            onLocationChange(newPos.lat, newPos.lng);
          }
        },
      }), [onLocationChange]);
  
    const { LayersControl, TileLayer, Marker, Popup, LayerGroup } = require('react-leaflet');
    const L = require('leaflet'); 
  
    // 🎯 ماركر الموقع الرئيسي (أزرق كبير)
    const createMainIcon = () => {
      return L.divIcon({
        className: 'bg-transparent border-none', 
        html: `
          <div class="relative flex items-center justify-center w-14 h-14">
            <div class="absolute w-10 h-10 bg-blue-500 rounded-full animate-ping opacity-60"></div>
            <div class="relative z-10 flex items-center justify-center w-6 h-6 bg-blue-700 rounded-full border-4 border-white shadow-lg"></div>
          </div>
        `,
        iconSize: [56, 56], iconAnchor: [28, 28], popupAnchor: [0, -14] 
      });
    };

    // 🎯 ماركر الأصول التابعة (أخضر/أحمر صغير)
    const createAssetIcon = (assetStatus) => {
      const isBroken = assetStatus === 'BROKEN';
      const mainColor = isBroken ? 'bg-red-600' : 'bg-emerald-500';
      const pulseColor = isBroken ? 'bg-red-500' : 'bg-emerald-400';
      return L.divIcon({
        className: 'bg-transparent border-none', 
        html: `
          <div class="relative flex items-center justify-center w-10 h-10">
            <div class="absolute w-6 h-6 ${pulseColor} rounded-full animate-ping opacity-40"></div>
            <div class="relative z-10 flex items-center justify-center w-3.5 h-3.5 ${mainColor} rounded-full border-[2px] border-white shadow-md"></div>
          </div>
        `,
        iconSize: [40, 40], iconAnchor: [20, 20], popupAnchor: [0, -10] 
      });
    };

    return (
      <MapContainer center={center} zoom={18} style={{ height: '100%', width: '100%' }}>
        <LayersControl position="topright">
          <LayersControl.BaseLayer name="OpenStreetMap">
            <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          </LayersControl.BaseLayer>
          
          {/* 🎯 السحر هنا: دمجنا القمر الصناعي مع أسماء الشوارع والمناطق (Hybrid) */}
          <LayersControl.BaseLayer checked name="Satellite (Hybrid)">
            <LayerGroup>
              {/* 1. طبقة القمر الصناعي (الأساس) */}
              <TileLayer 
                attribution='&copy; Esri' 
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" 
              />
              {/* 2. طبقة أسماء المدن والمناطق */}
              <TileLayer 
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}" 
              />
              {/* 3. طبقة خطوط الشوارع والطرق */}
              <TileLayer 
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}" 
              />
            </LayerGroup>
          </LayersControl.BaseLayer>
        </LayersControl>
  
        {/* الماركر الرئيسي للموقع */}
        <Marker draggable={true} eventHandlers={eventHandlers} position={center} ref={markerRef} icon={createMainIcon()}>
          <Popup closeButton={false}>
            <div className={`flex flex-col gap-2 min-w-[160px] p-1 font-sans ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? "rtl" : "ltr"}>
              <div className={`flex justify-between items-center border-b border-gray-200 pb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span className="font-black text-blue-900 text-[13px] tracking-wider">{locationId}</span>
                <span className="text-[9px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-black">{t('locationDetails.lblLocation', 'LOCATION')}</span>
              </div>
              <p className="text-[10px] text-gray-500 font-bold uppercase">{t('assets.map.gps_location', 'GPS LOCATION')}</p>
              {bim_id && (
                <button onClick={() => onOpen3D(bim_id)} className={`mt-1 bg-blue-900 hover:bg-blue-700 text-white text-[11px] font-bold py-2 px-3 rounded flex items-center justify-center gap-2 transition-all w-full shadow-md ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Globe size={14} /> {t('assets.bim.show_3d', 'VIEW 3D')}
                </button>
              )}
            </div>
          </Popup>
        </Marker>

        {/* 🎯 السحر هنا: ماركرات الأصول التابعة للموقع */}
        {childAssets?.filter(a => a.lat && a.lng).map((asset, idx) => (
           <Marker key={idx} position={[asset.lat, asset.lng]} icon={createAssetIcon(asset.status)}>
             <Popup closeButton={false}>
               <div className={`flex flex-col gap-2 min-w-[160px] p-1 font-sans ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? "rtl" : "ltr"}>
                 <div className={`flex justify-between items-center border-b border-gray-200 pb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                   <span className="font-black text-[#1a3353] text-[13px] tracking-wider">{asset.assetnum}</span>
                   <span className={`w-2.5 h-2.5 rounded-full animate-pulse shadow-sm ${asset.status === 'BROKEN' ? 'bg-red-600 shadow-red-500/50' : 'bg-green-500 shadow-green-500/50'}`}></span>
                 </div>
                 <p className="text-[10px] text-gray-500 font-bold uppercase truncate">{asset.description}</p>
                 {asset.bim_id && (
                   <button onClick={() => onOpenAsset3D(asset.asset_id)} className={`mt-1 bg-[#1a3353] hover:bg-blue-600 text-white text-[11px] font-bold py-2 px-3 rounded flex items-center justify-center gap-2 transition-all w-full shadow-md ${isRTL ? 'flex-row-reverse' : ''}`}>
                     <Box size={14} /> {t('assets.bim.show_asset', 'VIEW ASSET')}
                   </button>
                 )}
               </div>
             </Popup>
           </Marker>
        ))}
      </MapContainer>
    );
  };
  
const LocationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const locationPath = useLocation();
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;
  const changeFontSize = fontContext?.changeFontSize || (() => {});
  const [nav, setNav] = useState({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

  const [location, setLocation] = useState({
    location_id: '',
    description: '',
    status: 'OPERATING',
    type: 'OPERATING',
    site_id: sessionSite,
    org_id: sessionOrg,
    parent_id: '',
    address: '',
    classstructureid: '',
    model_urn: '',
    bim_id: '', // Added bim_id
    bim_space_id: '',
    image_url: '',
    lat: 30.0444, lng: 31.2357,
    specifications: [],
    hazards: [],
    assets: [],
    audit_logs: [],
    meters: [],
    safetypnum: '',
    safety_hazards: [], 
    loto_steps: [],
  });

  const [activeTab, setActiveTab] = useState('Location');
  const [showClassTree, setShowClassTree] = useState(false);
  const [showLookup, setShowLookup] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [fieldMaps, setFieldMaps] = useState({});
  const [isMeterModalOpen, setIsMeterModalOpen] = useState(false);
  const [isMeterHistoryOpen, setIsMeterHistoryOpen] = useState(false);
  const [showIncidentModal , setShowIncidentModal] = useState(false);
  const [showIncidentHistory, setShowIncidentHistory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);
  const [selectedMeterIdx, setSelectedMeterIdx] = useState(0);
  const [activeBimGuid, setActiveBimGuid] = useState(null);

  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', domainId: '', field: '' });
  const [showStatusModal, setShowStatusModal] = useState(false); 
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const queryParams = new URLSearchParams(locationPath.search).toString();
  // --- Work Orders History State ---
  const [woHistory, setWoHistory] = useState([]);
  const [loadingWoHistory, setLoadingWoHistory] = useState(false);

  const fetchLocationWOHistory = useCallback(async () => {
    if (!location.location_id || isNew) return;
    setLoadingWoHistory(true);
    try {
      const token = localStorage.getItem('token');
      // بننادي على API أوامر الشغل ونفلتر باللوكيشن الحالي
      const res = await axios.get('/api/workorders', {
        params: { location_id: location.location_id, limit: 50, sortBy: 'wonum', sortDir: 'desc' },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setWoHistory(res.data.data || []);
    } catch (err) {
      console.error("Failed to load WO history", err);
      toast.error(t('locationDetails.woHistoryFail', 'Failed to load Work Orders'));
    } finally {
      setLoadingWoHistory(false);
    }
  }, [location.location_id, isNew, t]);

  // مراقبة التابة لعمل Fetch
  useEffect(() => {
    if (activeTab === 'Work Orders') {
      fetchLocationWOHistory();
    }
  }, [activeTab, fetchLocationWOHistory]);

  // دالة مساعدة لتنسيق التاريخ لو مش موجودة عندك
  const formatDateTime = (dateString) => {
      if (!dateString) return '---';
      return dateString.split('.')[0].substring(0, 16).replace('T', ' ');
  };


  // 🎯 المسميات تم إصلاحها وإضافة Geomap
  const tabNames = {
      'Location': t('locationDetails.tabLocation', 'Location'),
      'Assets': t('locationDetails.tabAssets', 'Assets'),
      'Work Orders': t('wo.tabs.workOrder', 'Work Orders'),
      'Specifications': t('locationDetails.tabSpecs', 'Specifications'),
      'Safety': t('locationDetails.tabSafety', 'Safety'),
      'Meters': t('locationDetails.tabMeters', 'Meters'),
      'Geomap': t('assets.tabs.map', 'Geomap'), 
      '3D View': t('locationDetails.tab3D', '3D View'),
      'History': t('locationDetails.tabHistory', 'History')
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [locationMaps, meterMaps] = await Promise.all([
          axios.get('/api/admin/field-map/Locations'),
          axios.get('/api/admin/field-map/Meters')
        ]);
        setFieldMaps({ ...(locationMaps.data || {}), ...(meterMaps.data || {}) });

        if (id && id !== 'new') {
          const res = await axios.get(`/api/locations/${id}`);
          setLocation(res.data);
        }
      } catch (err) { console.error("Error loading data:", err); }
      finally { setLoading(false); }
    };
    loadData();
  }, [id]);

  useEffect(() => {
    if (activeTab === '3D View' && location.bim_id) {
      setActiveBimGuid(location.bim_id, { timestamp: Date.now() });
    }
  }, [activeTab, location.bim_id]);

  const FontControls = () => {
    const { fontSize, changeFontSize } = useFontSize();
    return (
      <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/20 ml-4">
        <button onClick={() => changeFontSize(-1)} className="hover:text-orange-400 transition-colors"><Minus size={14} /></button>
        <div className="flex items-center gap-1 min-w-[40px] justify-center"><Type size={14} /><span className="font-black text-[10px]">{fontSize}</span></div>
        <button onClick={() => changeFontSize(1)} className="hover:text-orange-400 transition-colors"><Plus size={14} /></button>
      </div>
    );
  };

  const handleShowInBIM = () => {
    if (location.bim_id) {
      setActiveTab('3D View');
      setActiveBimGuid(location.bim_id);
    } else {
      toast.error(t('locationDetails.noBimLinked', 'No 3D Model Linked')); 
    }
  };

  useEffect(() => {
    const openStatusModal = () => setShowStatusModal(true);
    window.addEventListener('GLOBAL_STATUS_HANDLER', openStatusModal);
    return () => window.removeEventListener('GLOBAL_STATUS_HANDLER', openStatusModal);
  }, []);

  useEffect(() => {
    const handleOpenHistory = () => setShowHistoryModal(true);
    window.addEventListener('OPEN_STATUS_HISTORY', handleOpenHistory);
    return () => window.removeEventListener('OPEN_STATUS_HISTORY', handleOpenHistory);
  }, []);

  useEffect(() => {
    window.addEventListener('GLOBAL_DELETE_HANDLER', handleDelete);
    return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', handleDelete);
  }, [id, navigate]);

  useEffect(() => {
    const openMeterReading = () => setIsMeterModalOpen(true);
    window.addEventListener('GLOBAL_METER_READING_HANDLER', openMeterReading);
    return () => window.removeEventListener('GLOBAL_METER_READING_HANDLER', openMeterReading);
  }, []);

  useEffect(() => {
      const handler = () => setShowIncidentHistory(true);
      window.addEventListener('OPEN_INCIDENT_HISTORY', handler);
      return () => window.removeEventListener('OPEN_INCIDENT_HISTORY', handler);
  }, []);

  const handleSmartSearch = (fieldName, title, defaultType, onCustomSelect = null) => {
    const linkedDomain = (fieldMaps || {})[fieldName];
    setLookup({ 
      isOpen: true, title, type: linkedDomain ? 'DOMAIN' : defaultType, domainId: linkedDomain || '', field: fieldName, onCustomSelect: onCustomSelect 
    });
  };

  const handleClassSelect = async (node) => {
    try {
      const res = await axios.get(`/api/classifications/${node.classstructureid}/attributes`);
      const newSpecs = res.data.map(attr => ({
        assetattrid: attr.assetattrid, description: attr.description, alnvalue: '', numvalue: null, uom: attr.uom
      }));
      setLocation(prev => ({ ...prev, classstructureid: node.classstructureid, specifications: newSpecs }));
      setShowClassTree(false);
    } catch (err) { toast.error(t('locationDetails.failedFetchAttr', 'Failed to fetch attributes')); }
  };

  const loadNavigation = useCallback(async () => {
      if (!id || id === 'new') return;
      try {
          const res = await axios.get(`/api/locations/${id}/navigation${locationPath.search}`);
          setNav(res.data);
      } catch (err) { console.error("Nav load failed"); }
  }, [id, locationPath.search]);
  
  useEffect(() => { loadNavigation(); }, [loadNavigation]);
  
  const goTo = (targetId) => { if (targetId) navigate(`/locations/${targetId}${locationPath.search}`); };
  const handleInputChange = (field, value) => { setLocation(prev => ({ ...prev, [field]: value })); };
  
  const handleSpecUpdate = (idx, field, val) => {
    const updated = [...location.specifications];
    updated[idx] = { ...updated[idx], [field]: val };
    setLocation(prev => ({ ...prev, specifications: updated }));
  };

  const addHazardFromLookup = () => {
    handleSmartSearch('hazard_id', t('locationDetails.lblSelectHazards', 'Select Hazards'), 'hazards', (item) => {
      const exists = location.hazards.find(h => h.hazard_id === item.hazard_id);
      if (!exists) {
        setLocation(prev => ({ ...prev, hazards: [...prev.hazards, { hazard_id: item.hazard_id, description: item.description, precaution: item.precaution }] }));
      }
    });
  };

  const handleSave = async () => {
    if (!location.location_id || location.location_id.trim() === "") {
      toast.error(t('locationDetails.locMandatory', 'Location ID is mandatory')); return;
    }
    const cleanMeters = (location.meters || []).map(m => ({
        ...m, description: m.description || m.meter_name || "", last_reading_date: m.last_reading_date === "" ? null : m.last_reading_date, last_reading: m.last_reading === "" ? 0 : parseFloat(m.last_reading)
    }));
    const payload = { ...location, meters: cleanMeters };
    const loadId = toast.loading(t('locationDetails.saving', 'Saving...')); 

    api.post('/locations/save', payload)
      .then(res => {
          toast.success(t('locationDetails.saveSuccess', 'Saved Successfully'), { id: loadId }); 
          if (id === 'new') navigate(`/locations/${res.data.id}`);
          })
      .catch(err => { toast.error(`${t('locationDetails.saveFailed', 'Save Failed')} ${err.message}`, { id: loadId }); });
  };

  useEffect(() => {
    const triggerSave = () => handleSave();
    window.addEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
    return () => window.removeEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
  }, [handleSave]);

  const fetchCount = useCallback(async () => {
    if (!location.location_id || isNew) { setCount(0); return; }
    try {
      const res = await axios.get(`/api/attachments/count/LOCATION/${location.location_id}`);
      setCount(res.data.count || 0);
    } catch (err) { console.error("Error fetching attachment count:", err); }
  }, [location.location_id, isNew]);

  useEffect(() => { fetchCount(); }, [location.location_id, fetchCount]);

  useEffect(() => {
    const handler = () => setIsMeterHistoryOpen(true);
    window.addEventListener('OPEN_METER_HISTORY', handler);
    return () => window.removeEventListener('OPEN_METER_HISTORY', handler);
  }, []);

  const handleDelete = async () => {
        const result = await Swal.fire({
            title: t('locationDetails.deleteTitle', 'Are you sure?'), 
            text: t('locationDetails.deleteText', { id: location.location_id }), 
            icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', cancelButtonColor: '#3085d6',
            confirmButtonText: t('locationDetails.btnYesDelete', 'Yes, delete it!'), 
            cancelButtonText: t('locationDetails.btnCancel', 'Cancel'),
            customClass: { popup: 'font-sans text-[12px]' }
        });

        if (result.isConfirmed) {
          const loadId = toast.loading(t('locationDetails.deleting', 'Deleting...')); 
          try {
            await api.delete(`/locations/${id}`); 
            toast.success(t('locationDetails.deleteSuccess', 'Deleted Successfully'), { id: loadId }); 
            navigate('/locations'); 
          } catch (err) {
            const msg = err.response?.data?.detail || t('locationDetails.deleteFailed', 'Delete Failed'); 
            toast.error(msg, { id: loadId });
          }
        }
    };

  const handleRevitUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('location_id', location.location_id);
    
    const loadId = toast.loading(t('locationDetails.uploadingRevit', 'Uploading Revit Model...')); 
    setIsUploading(true);

    try {
      const res = await axios.post('/api/bim/upload', formData);
      setLocation(prev => ({ ...prev, model_urn: res.data.urn }));
      toast.success(t('locationDetails.revitSuccess', 'Revit Model Linked!'), { id: loadId }); 
    } catch (err) {
      toast.error(`${t('locationDetails.uploadFailed', 'Upload Failed')} ${err.message}`, { id: loadId }); 
    } finally { setIsUploading(false); }
  };

  const addNewMeter = () => {
    const newMeter = { meter_num: '', meter_name: '', meter_type: 'CONTINUOUS', unit_of_measure: 'EACH', active: true, last_reading: 0, last_reading_date: '', remarks: '' };
    setLocation(prev => {
        const oldMeters = Array.isArray(prev.meters) ? prev.meters : [];
        const updatedMeters = [...oldMeters, newMeter];
        setSelectedMeterIdx(updatedMeters.length - 1);
        return { ...prev, meters: updatedMeters };
    });
  };

  const deleteMeter = (idx) => {
    const updatedMeters = location.meters.filter((_, i) => i !== idx);
    setLocation(prev => ({ ...prev, meters: updatedMeters }));
    if (selectedMeterIdx >= updatedMeters.length) setSelectedMeterIdx(0);
  };

  const handleMeterUpdate = (fieldName, value) => {
    if (selectedMeterIdx === null || selectedMeterIdx === undefined) return;
    setLocation(prev => {
      const updatedMeters = [...(prev.meters || [])];
      updatedMeters[selectedMeterIdx] = { ...updatedMeters[selectedMeterIdx], [fieldName]: value };
      return { ...prev, meters: updatedMeters };
    });
  };
  
  if (loading) return <div className="p-20 text-center animate-pulse font-black text-[#005495]">{t('locationDetails.loadingCtx', 'Loading...')}</div>; 
  
  return (
    <div className={`flex flex-col h-screen bg-[#f3f4f6] text-[11px] overflow-hidden ${isRTL ? 'font-cairo' : 'font-sans'}"`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* البار العلوي */}
      <div className="bg-[#1a3353] text-white p-2 flex justify-between items-center shadow-lg px-4">
        <div className={`flex items-center gap-4 ${isRTL ? 'mr-2' : 'ml-2'}`}>
          <button onClick={() => navigate(`/locations${queryParams ? '?' + queryParams : ''}`)} className="hover:bg-white/10 p-1.5 rounded-full transition-all">
           {isRTL ? <ChevronRight size={18} /> : <ArrowLeft size={18}/>}
          </button>
          {!isNew && (
          <div className="flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1">
            <button disabled={!nav.prev} onClick={() => goTo(nav.first)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
            <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
            <div className="px-2 text-[10px] font-mono border-x border-white/10 mx-1">
              <span className="text-orange-400 font-black">{nav.current_pos}</span> / {nav.total}
            </div>
            <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
            <button disabled={!nav.next} onClick={() => goTo(nav.last)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
          </div>
        )}
        </div>

        <div className={`flex items-center gap-1 ${isRTL ? 'ml-2' : 'mr-2'}`}>
          <button onClick={handleSave} className="flex items-center gap-2 bg-green-600 hover:bg-green-700 px-3 py-1.5 rounded-sm font-bold transition-all shadow-md">
            <Save size={14}/> {t('locationDetails.btnSave', 'SAVE')}
          </button>
          
          <div className="w-[1px] h-6 bg-white/10 mx-1" />

          <SmartPrintButton 
            moduleName="LOCATION" 
            refId={location.location_id} 
            extraParams={{ location_id: location.location_id, report_type: 'GL_SUMMARY' }} 
          />

          <button onClick={() => setShowStatusModal(true)} title={t('locationDetails.tooltipChangeStatus', 'Change Status')} className="p-2 hover:bg-white/10 rounded-sm">
            <Activity size={16} className="text-yellow-400" />
          </button>

          <button onClick={() => setShowHistoryModal(true)} title={t('locationDetails.tooltipStatusHistory', 'Status History')} className="p-2 hover:bg-white/10 rounded-sm">
            <History size={16} className="text-blue-300" />
          </button>

          <button onClick={() => setShowAttachments(true)} className="relative p-2 hover:bg-white/10 rounded text-blue-200">
            <Paperclip size={16} className="text-gray-300" />
            {count > 0 && <span className="absolute top-1 right-1 bg-red-600 text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black animate-pulse">{count}</span>}
          </button>

          <button onClick={handleDelete} title={t('locationDetails.tooltipDelete', 'Delete')} className="p-2 hover:bg-red-600 rounded-sm transition-colors">
            <Trash2 size={16} />
          </button>
          <FontControls />
        </div>
      </div>
      
      {/* Top Header Toolbar */}
      <div className="bg-white p-2 border-b flex justify-between items-center shadow-sm z-20">
        <div className={`flex items-center gap-4 ${isRTL ? 'mr-4' : 'ml-4'}`}>
          <HeaderField label={t('locationDetails.lblLocation', 'LOCATION')} value={location.location_id} isBold isRequired onChange={v => setLocation({...location, location_id: v})} />
          <HeaderField label={t('locationDetails.lblDescription', 'DESCRIPTION')} value={location.description} width="w-[450px]" onChange={v => setLocation({...location, description: v})} />
          {location.bim_id && <button onClick={handleShowInBIM} className={`flex items-center gap-1 text-blue-600 hover:text-blue-900 ${isRTL ? 'border-r pr-4' : 'border-l pl-4'}`}><Box size={14}/> {t('locationDetails.btnRevitLinked', '3D Linked')}</button>}
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex bg-[#1a3353] text-white overflow-x-auto shadow-md">
        {['Location', 'Assets', 'Work Orders', 'Specifications', 'Safety', 'Meters', 'Geomap', '3D View'].map(tab => (
          <button 
            key={tab} 
            onClick={() => setActiveTab(tab)} 
            className={`px-8 py-2.5 flex items-center gap-2 font-bold ${isRTL ? 'border-l border-white/5' : 'border-r border-white/5'} transition-all uppercase tracking-tighter
            ${activeTab === tab ? 'bg-white text-[#1a3353]' : 'hover:bg-white/10 opacity-75'}`}
          >
            {tab === 'Location' && <MapPin size={14}/>}
            {tab === 'Assets' && <Box size={14}/>}
            {tab === 'Work Orders' && <Briefcase size={14}/>}
            {tab === 'Specifications' && <List size={14}/>}
            {tab === 'Safety' && <ShieldAlert size={14}/>}
            {tab === 'Meters' && <Activity size={13}/> }
            {tab === 'Geomap' && <MapPin size={13}/> }
            {tab === '3D View' && <Globe size={14}/>}
            {tab === 'History' && <History size={14}/>}
            {tabNames[tab]}
          </button>
        ))}
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto p-4 custom-scrollbar bg-[#f8f9fb]">
        <div className="max-w-7xl mx-auto pb-20">
          
          {/* TAB: Location */}
          {activeTab === 'Location' && (
            <div className="grid grid-cols-12 gap-6 animate-in fade-in duration-300">
              <div className="col-span-8 space-y-6">
                <SectionCard title={t('locationDetails.secIdentity', 'Identity')}>
                  <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                    <DataField label={t('locationDetails.lblStatus', 'Status')} value={location.status} isHighlight />
                    <EditableField label={t('locationDetails.lblType', 'Type')} value={location.type} hasList onSearch={() => handleSmartSearch('type', 'LOCTYPE', 'DOMAIN')} />
                    <EditableField label={t('locationDetails.lblParentLoc', 'Parent Location')} value={location.parent_id} onSearch={() => handleSmartSearch('parent_id', 'Select Parent', 'locations')} />
                    <SearchField label={t('locationDetails.lblSiteID', 'Site')} value={location.site_id} onChange={(val) => handleInputChange('site_id', val)} onSearch={() => handleSmartSearch('site_id', 'Sites', 'sites')} />
                  </div>
                </SectionCard>
                <SectionCard title={t('locationDetails.secGeoInfo', 'Geographic Info')}>
                  <div className="grid grid-cols-1 gap-4">
                    <EditableField label={t('locationDetails.lblServiceAddr', 'Address')} value={location.address} onChange={v => setLocation({...location, address: v})} />
                    <div className="grid grid-cols-2 gap-x-12 mt-2">
                       <DataField label={t('locationDetails.lblBimSpaceId', 'BIM Space ID')} value={location.bim_space_id} />
                       <DataField label={t('locationDetails.lblGisCoords', 'GPS')} value={`${location.lat}, ${location.lng}`} />
                    </div>
                  </div>
                </SectionCard>
              </div>
              <div className="col-span-4">
                <SectionCard title={t('locationDetails.secRefImage', 'Image')}>
                   <div className="h-48 bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-gray-100 transition-all">
                      <Camera size={40} className="text-gray-300 mb-2" />
                      <span className="text-gray-400 font-bold uppercase text-[9px]">{t('locationDetails.btnUploadPhoto', 'Upload')}</span>
                   </div>
                </SectionCard>
              </div>
            </div>
          )}

          {/* TAB: Assets */}
          {activeTab === 'Assets' && (
            <div className="bg-white border shadow-sm rounded animate-in slide-in-from-right duration-300">
              <table className={`w-full ${isRTL ? 'text-right' : 'text-left'}`}>
                <thead className="bg-gray-100 border-b text-[#005495] font-black uppercase text-[10px]">
                  <tr><th className="p-3 border-x border-white">{t('locationDetails.thAssetNum', 'ASSET')}</th><th className="p-3 border-x border-white">{t('locationDetails.lblDescription', 'DESCRIPTION')}</th><th className="p-3 border-x border-white text-center">{t('locationDetails.thStatus', 'STATUS')}</th></tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {location.assets?.map((as, i) => (
                    <tr key={i} className="hover:bg-blue-50 cursor-pointer" onClick={() => navigate(`/assets/${as.asset_id}`)}>
                      <td className="p-3 border-x border-gray-50 font-bold text-blue-900 underline">{as.assetnum}</td>
                      <td className="p-3 border-x border-gray-50">{as.description}</td>
                      <td className="p-3 border-x border-gray-50 text-center">
                        <span className={`px-2 py-0.5 rounded-sm text-[9px] font-black ${as.status === 'OPERATING' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{as.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* TAB: Work Orders */}
          {activeTab === 'Work Orders' && (
            <div className="max-w-7xl mx-auto animate-in slide-in-from-right duration-500 pb-20">
              <SectionCard title={`${t('locationDetails.tabWorkOrders', 'Work Orders')} - ${location.location_id || '---'}`}>
                <div className="overflow-x-auto">
                  <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                    <thead className="bg-slate-50 border-b text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      <tr>
                        <th className="p-4">{t('wo.fields.wonum', 'WO Number')}</th>
                        <th className="p-4">{t('wo.fields.description', 'Description')}</th>
                        <th className="p-4">{t('common.status', 'Status')}</th>
                        <th className="p-4">{t('wo.fields.workType', 'Work Type')}</th>
                        <th className="p-4 text-center">{t('wo.fields.schedStart', 'Date')}</th>
                        <th className="p-4 text-center">{t('common.actions', 'Action')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {loadingWoHistory ? (
                        <tr>
                          <td colSpan="6" className="p-12 text-center text-blue-500 font-bold animate-pulse uppercase tracking-widest">
                            {t('common.loading', 'Loading Data...')}
                          </td>
                        </tr>
                      ) : !location.location_id ? (
                        <tr>
                          <td colSpan="6" className="p-12 text-center text-gray-400 italic">
                            <div className="flex flex-col items-center gap-2">
                              <Briefcase size={32} className="opacity-20" />
                              {t('locationDetails.noLocSaved', 'Save location first to view records.')}
                            </div>
                          </td>
                        </tr>
                      ) : woHistory.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="p-12 text-center text-gray-400 italic">
                            <div className="flex flex-col items-center gap-2">
                              <Briefcase size={32} className="opacity-20" />
                              {t('locationDetails.emptyWOHistory', 'No work orders found for this location.')}
                            </div>
                          </td>
                        </tr>
                      ) : (
                        woHistory.map((wo) => (
                          <tr key={wo.wonum} className="hover:bg-blue-50/50 transition-colors group text-[11px] font-bold">
                            <td className="p-4 font-black text-blue-900">{wo.wonum}</td>
                            <td className="p-4 text-gray-600">{wo.description}</td>
                            <td className="p-4">
                              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-[9px] font-black uppercase shadow-sm border border-blue-200">
                                {wo.status}
                              </span>
                            </td>
                            <td className="p-4 font-black text-gray-400 text-[10px]">{wo.wo_type || 'CM'}</td>
                            <td className="p-4 text-center text-gray-500 font-mono">
                              {formatDateTime(wo.actstart || wo.targstartdate || wo.reportdate)}
                            </td>
                            <td className="p-4 text-center">
                              <button 
                                // 🎯 بيفتح أمر الشغل في Tab جديدة في المتصفح
                                onClick={() => window.open(`/workorders/${wo.wonum}`, '_blank')}
                                className="p-2 text-blue-600 hover:bg-blue-600 hover:text-white rounded transition-all"
                                title={t('wo.actions.viewWO', 'View Work Order')}
                              >
                                <Zap size={14} />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
              
              <div className={`mt-6 bg-orange-50 border-orange-400 p-4 rounded shadow-sm flex items-center gap-4 ${isRTL ? 'border-r-4' : 'border-l-4'}`}>
                <Briefcase className="text-orange-600" size={20} />
                <p className="text-[11px] text-orange-800 font-bold italic">
                  {t('locationDetails.woHistoryTip', 'Tip: Click the action button to open the work order in a new browser tab.')}
                </p>
              </div>
            </div>
          )}

          {/* TAB: Specifications */}
          {activeTab === 'Specifications' && (
            <div className="bg-white border shadow-sm animate-in slide-in-from-bottom duration-300">
              <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                <div className="w-1/2">
                    <EditableField label={t('locationDetails.lblClass', 'Classification')} value={location.classstructureid} onSearch={() => setShowClassTree(true)} />
                </div>
              </div>
              <table className={`w-full ${isRTL ? 'text-right' : 'text-left'}`}>
                <thead className="bg-[#1a3353] text-white font-bold uppercase text-[10px]">
                  <tr><th className="p-3 border-x border-white/10">{t('locationDetails.thAttr', 'ATTRIBUTE')}</th><th className="p-3 border-x border-white/10">{t('locationDetails.thDesc', 'DESCRIPTION')}</th><th className="p-3 border-x border-white/10">{t('locationDetails.thValue', 'VALUE')}</th><th className="p-3 w-10"></th></tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {location.specifications?.map((spec, i) => (
                    <tr key={i} className="hover:bg-blue-50">
                      <td className="p-2 border-x border-gray-50 font-bold text-blue-900">{spec.assetattrid}</td>
                      <td className="p-2 border-x border-gray-50 text-gray-500 italic">{spec.description}</td>
                      <td className="p-2 border-x border-gray-50 bg-blue-50/30">
                        <input className="w-full bg-transparent outline-none font-black text-blue-800" value={spec.alnvalue || ''} onChange={e => handleSpecUpdate(i, 'alnvalue', e.target.value)} />
                      </td>
                      <td className="p-2 text-center text-gray-300 hover:text-red-500 cursor-pointer"><Trash2 size={14} onClick={() => {
                        const updated = location.specifications.filter((_, idx) => idx !== i);
                        setLocation({...location, specifications: updated});
                      }}/></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* TAB: Safety */}
          {activeTab === 'Safety' && (
            <div className={`max-w-7xl mx-auto space-y-6 animate-in slide-in-from-right duration-500 pb-20 ${isRTL ? 'text-right' : 'text-left'}`}>
              
              {/* 1. المرتبط بـ Safety Plans (خطة السلامة) */}
              <SectionCard title={t('assets.safety.plan_card', 'SAFETY PLAN')}>
                <div className={`flex items-center justify-between bg-blue-50 p-4 border border-blue-100 rounded-sm shadow-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="p-3 bg-[#1a3353] text-white rounded-sm shadow-md">
                      <ShieldAlert size={24} />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">{t('assets.safety.active_plan', 'ACTIVE PLAN')}</p>
                      <p className="text-sm font-black text-[#1a3353]">{location.safetypnum || t('assets.safety.no_plan', 'No Plan Assigned')}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => handleSmartSearch('safetypnum', t('assets.safety.link_plan', 'LINK PLAN'), 'safetyplans', async (selectedPlan) => {
                      const planNum = selectedPlan.safetypnum;
                      try {
                        const loadId = toast.loading(t('assets.safety.applying_proto', 'Applying Safety Protocols...'));
                        setLocation(prev => ({ ...prev, safetypnum: planNum }));
                        
                        // 🎯 جلب تفاصيل خطة السلامة من الباك إند
                        const res = await axios.get(`/api/safety-plans/${planNum}`);
                        
                        // 🎯 الفلترة والـ Mapping الصحيح بناءً على شكل الـ API بتاعك
                        const fetchedHazards = res.data.hazards || res.data.safety_hazards || [];
                        const fetchedLoto = (res.data.tagouts || res.data.woloto || []).map(tag => ({
                            tagoutnum: tag.tagout_id || tag.tagoutnum || 'UNKNOWN',
                            description: tag.description || '',
                            location: tag.location || 'At Location',
                            required_state: tag.required_state || 'OFF'
                        }));

                        setLocation(prev => ({
                          ...prev,
                          safety_hazards: fetchedHazards,
                          loto_steps: fetchedLoto
                        }));
                        
                        toast.success(t('assets.toasts.save_success', 'Applied Successfully'), { id: loadId });
                      } catch (err) {
                        handleInputChange('safetypnum', planNum);
                        toast.error(t('assets.toasts.load_fail', 'Failed to load details'), { id: loadId });
                      }
                    })}
                    className="bg-white border border-[#1a3353] text-[#1a3353] px-4 py-1.5 rounded-sm font-black text-[10px] hover:bg-[#1a3353] hover:text-white transition-all uppercase shadow-sm"
                  >
                    {location.safetypnum ? t('assets.safety.change_plan', 'CHANGE PLAN') : t('assets.safety.link_plan', 'LINK PLAN')}
                  </button>
                </div>
              </SectionCard>
          
              {/* 2. Hazards Table (جدول المخاطر) */}
              <SectionCard title={t('assets.safety.hazard_title', 'HAZARDS')}>
                <div className="bg-white border rounded shadow-sm overflow-hidden">
                  <table className={`w-full text-left ${isRTL ? 'text-right' : 'text-left'}`}>
                    <thead className="bg-[#f8fafc] border-b text-gray-700 text-[10px] font-black uppercase">
                      <tr>
                        <th className="p-3 border-x border-gray-100 w-40">{t('assets.safety.hazard_num', 'HAZARD ID')}</th>
                        <th className="p-3 border-x border-gray-100">{t('locationDetails.lblDescription', 'DESCRIPTION')}</th>
                        <th className="p-3 border-x border-gray-100">{t('assets.safety.hazard_type', 'TYPE')}</th>
                        <th className="p-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {location.safety_hazards?.length > 0 ? (
                        location.safety_hazards.map((h, i) => (
                          <tr key={i} className="hover:bg-red-50/20 transition-colors">
                            <td className={`p-3 border-x border-gray-50 font-black text-red-700 ${isRTL ? 'text-right' : ''}`}>{h.hazardnum || h.hazard_id}</td>
                            <td className={`p-3 border-x border-gray-50 font-semibold text-gray-600 uppercase italic text-[11px] ${isRTL ? 'text-right' : ''}`}>{h.description}</td>
                            <td className="p-3 border-x border-gray-50">
                              <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-[9px] font-black uppercase">
                                {h.hazardtype || 'SAFETY'}
                              </span>
                            </td>
                            <td className="p-2 text-center">
                              <Trash2 size={14} className="text-gray-300 hover:text-red-600 cursor-pointer mx-auto" onClick={() => {
                                const updated = location.safety_hazards.filter((_, idx) => idx !== i);
                                setLocation({...location, safety_hazards: updated});
                              }}/>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="p-8 text-center text-gray-300 italic">{t('assets.safety.no_hazards', 'No hazards identified')}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <button className={`m-3 text-[10px] font-black text-blue-700 uppercase flex items-center gap-1 hover:underline ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Plus size={12}/> {t('assets.safety.add_hazard', 'ADD HAZARD')}
                  </button>
                </div>
              </SectionCard>
          
              {/* 3. LOTO Table (العزل وإغلاق الطاقة) */}
              <SectionCard title={t('assets.safety.loto_title', 'LOCKOUT / TAGOUT')}>
                <div className="bg-white border rounded shadow-sm overflow-hidden">
                  <table className={`w-full text-[11px] ${isRTL ? 'text-right' : 'text-left'}`}>
                    <thead className="bg-[#f8fafc] border-b text-gray-700 text-[10px] font-black uppercase">
                      <tr>
                        <th className="p-3 border-x border-gray-100 w-32">{t('assets.safety.tag_id', 'TAG ID')}</th>
                        <th className="p-3 border-x border-gray-100">{t('locationDetails.lblDescription', 'DESCRIPTION')}</th>
                        <th className="p-3 border-x border-gray-100 w-32 text-center">{t('assets.safety.req_state', 'REQUIRED STATE')}</th>
                        <th className="p-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 font-bold uppercase">
                      {location.loto_steps?.length > 0 ? (
                        location.loto_steps.map((l, i) => (
                          <tr key={i} className="hover:bg-blue-50/50 transition-colors">
                            <td className={`p-3 border-x border-gray-50 text-blue-900 font-black ${isRTL ? 'text-right' : ''}`}>{l.tagoutnum}</td>
                            <td className={`p-3 border-x border-gray-50 ${isRTL ? 'text-right' : ''}`}>
                              <p>{l.description}</p>
                              <p className={`text-[9px] text-gray-400 mt-1 flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <MapPin size={10}/> {l.location || 'At Location'}
                              </p>
                            </td>
                            <td className="p-3 border-x border-gray-50 text-center">
                              <span className="text-red-600 font-black">{l.required_state || 'OFF'}</span>
                            </td>
                            <td className="p-2 text-center">
                              <Trash2 size={14} className="text-gray-300 hover:text-red-600 cursor-pointer mx-auto" onClick={() => {
                                const updated = location.loto_steps.filter((_, idx) => idx !== i);
                                setLocation({...location, loto_steps: updated});
                              }}/>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="p-8 text-center text-gray-300 italic">{t('assets.safety.no_loto', 'No LOTO steps defined')}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <button className={`m-3 text-[10px] font-black text-blue-700 uppercase flex items-center gap-1 hover:underline ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Plus size={12}/> {t('assets.safety.add_loto', 'ADD LOTO')}
                  </button>
                </div>
              </SectionCard>
            </div>
          )}

          {/* TAB: Meters */}
          {activeTab === 'Meters' && (
            <div className="max-w-7xl mx-auto space-y-4 animate-in slide-in-from-bottom-4 duration-500">
              <div className="bg-white border rounded shadow-sm overflow-hidden">
                <div className={`p-1 bg-gray-100 font-bold border-b text-[10px] flex justify-between items-center px-3 text-gray-500 uppercase tracking-widest ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Activity size={14} className="text-blue-600"/>
                    <span>{t('locationDetails.titleLocMeters', 'METERS')}</span>
                  </div>
                </div>
                
                <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                  <thead className="bg-[#f9fafb] border-b text-gray-400 font-bold text-[9px] uppercase tracking-tighter">
                    <tr>
                      <th className="p-2 w-8 border-x border-white"></th>
                      <th className="p-2 border-x border-gray-200 w-16">{t('locationDetails.thSeq', 'SEQ')}</th>
                      <th className="p-2 border-x border-gray-200 text-blue-700">{t('locationDetails.thMeterNum', 'METER')}</th>
                      <th className="p-2 border-x border-gray-200">{t('locationDetails.thDesc', 'DESCRIPTION')}</th>
                      <th className="p-2 border-x border-gray-200">{t('locationDetails.thType', 'TYPE')}</th>
                      <th className="p-2 border-x border-gray-200">{t('locationDetails.thUOM', 'UOM')}</th>
                      <th className="p-2 border-x border-gray-200 text-center">{t('locationDetails.thActive', 'ACTIVE')}</th>
                      <th className="p-2 w-10 border-x border-white"></th>
                    </tr>
                  </thead>
                  <tbody className="text-[11px] divide-y divide-gray-100">
                    {(location.meters || []).length > 0 ? (
                      location.meters.map((m, i) => (
                        <tr 
                          key={i} 
                          onClick={() => setSelectedMeterIdx(i)} 
                          className={`hover:bg-blue-50/50 cursor-pointer transition-colors ${selectedMeterIdx === i ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''}`}
                        >
                          <td className="p-2 text-center text-blue-600">
                            {selectedMeterIdx === i && <ChevronRight size={14} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/>}
                          </td>
                          <td className="p-2 border-x border-gray-50 text-gray-400 font-mono">{i + 1}</td>
                          <td className="p-2 border-x border-gray-50 font-black text-[#005495] uppercase">{m.meter_num || t('locationDetails.newMeter', 'NEW METER')}</td>
                          <td className="p-2 border-x border-gray-50 font-semibold text-gray-600">{m.meter_name || '---'}</td>
                          <td className="p-2 border-x border-gray-50 text-[9px] font-bold text-gray-500 uppercase">{m.meter_type}</td>
                          <td className="p-2 border-x border-gray-50 text-gray-500 font-bold">{m.unit_of_measure}</td>
                          <td className="p-2 border-x border-gray-50 text-center">
                            <div className={`w-2 h-2 rounded-full mx-auto ${m.active ? 'bg-green-500 shadow-[0_0_5px_green]' : 'bg-gray-300'}`} />
                          </td>
                          <td className="p-2 text-center border-x border-white">
                            <button onClick={(e) => { e.stopPropagation(); deleteMeter(i); }} className="p-1 hover:bg-red-50 rounded text-gray-300 hover:text-red-600 transition-colors">
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="p-10 text-center text-gray-400 italic font-bold uppercase tracking-widest bg-gray-50/50">
                          {t('locationDetails.clickNewRow', 'CLICK + TO ADD A METER')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {selectedMeterIdx !== null && location.meters?.[selectedMeterIdx] && (
                <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                  <div className={`bg-[#1a3353] p-2 px-4 font-black text-white border-b-0 text-[10px] uppercase tracking-widest rounded-t-sm flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <span>{t('locationDetails.titleMeterConfig', 'METER CONFIGURATION')}</span>
                    <span className="text-blue-300">{location.meters[selectedMeterIdx].meter_num || t('locationDetails.newEntry', 'NEW ENTRY')}</span>
                  </div>
                  <div className={`bg-white border p-6 grid grid-cols-2 gap-x-16 gap-y-6 shadow-sm rounded-b-sm ${isRTL ? 'text-right' : 'text-left'}`}>
                    <div className="space-y-5">
                      <EditableField 
                        label={t('locationDetails.lblMeterNum', 'METER')} 
                        value={location.meters[selectedMeterIdx].meter_num} 
                        isRequired 
                        desc={location.meters[selectedMeterIdx].meter_name}
                        onChange={v => handleMeterUpdate('meter_num', v)}
                        onSearch={() => handleSmartSearch('meter_id', 'Select Meter Definition', 'meters', (selectedItem) => {
                          handleMeterUpdate('meter_num', selectedItem.meter_num);
                          handleMeterUpdate('meter_name', selectedItem.meter_name);
                          handleMeterUpdate('meter_type', selectedItem.meter_type);
                          handleMeterUpdate('unit_of_measure', selectedItem.unit_of_measure);
                        })} 
                      />
                      <EditableField label={t('locationDetails.lblMeterType', 'TYPE')} value={location.meters[selectedMeterIdx].meter_type} hasList onSearch={() => handleSmartSearch('meter_type', 'Meter Type', 'DOMAIN')} />
                      <EditableField label={t('locationDetails.lblUOM', 'UOM')} value={location.meters[selectedMeterIdx].unit_of_measure} onChange={v => handleMeterUpdate('unit_of_measure', v)} hasList onSearch={() => handleSmartSearch('uom', 'Units', 'DOMAIN')} />
                      <div className={`flex items-center gap-3 p-2 bg-gray-50 rounded border border-gray-100 w-fit ${isRTL ? 'pl-6' : 'pr-6'}`}>
                        <input type="checkbox" id="meterActive" checked={location.meters[selectedMeterIdx].active} onChange={e => handleMeterUpdate('active', e.target.checked)} className="accent-blue-700 w-4 h-4 cursor-pointer" />
                        <label htmlFor="meterActive" className="font-black text-[#1a3353] text-[10px] cursor-pointer uppercase">{t('locationDetails.lblMeterActive', 'ACTIVE')}</label>
                      </div>
                    </div>
                    <div className="space-y-5">
                      <div className="grid grid-cols-2 gap-4">
                        <StaticField label={t('locationDetails.lblLastReading', 'LAST READING')} value={location.meters[selectedMeterIdx].last_reading} />
                        <StaticField label={t('locationDetails.lblReadingDate', 'DATE')} value={location.meters[selectedMeterIdx].last_reading_date} />
                      </div>
                      <EditableField label={t('locationDetails.lblInspector', 'INSPECTOR')} value={location.meters[selectedMeterIdx].inspector} onSearch={() => handleSmartSearch('inspector', 'Select Inspector', 'PERSON')} />
                      <div className="flex flex-col gap-1.5">
                        <label className="text-gray-400 font-black text-[9px] uppercase tracking-widest">{t('locationDetails.lblRemarks', 'REMARKS')}</label>
                        <textarea className="border border-gray-200 p-2 outline-none h-20 resize-none text-[11px] focus:border-blue-500 transition-colors bg-gray-50/30 rounded" placeholder="..." value={location.meters[selectedMeterIdx].remarks || ''} onChange={e => handleMeterUpdate('remarks', e.target.value)} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className={`pt-2 ${isRTL ? 'flex justify-start' : 'flex justify-end'}`}>
                <button onClick={addNewMeter} className={`bg-[#005495] text-white px-8 py-2 rounded-sm font-black shadow-md hover:bg-[#1a3353] transition-all flex items-center gap-2 uppercase tracking-tighter text-[10px] ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Plus size={16} /> {t('locationDetails.btnAddMeterRow', 'NEW METER')}
                </button>
              </div>
            </div>
          )}

          {/* TAB: Geomap (Wow Factor) */}
          {activeTab === 'Geomap' && (
            <div className="h-[550px] border-4 border-white rounded shadow-xl overflow-hidden relative animate-in zoom-in-95 duration-500">
              <div className={`p-3 bg-white border-b flex justify-between items-center text-[#1a3353] font-black uppercase text-[10px] tracking-widest ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><MapPin size={14} className="text-blue-600"/> {t('assets.tabs.map', 'GEOMAP')}</div>
                <div className="flex gap-4">
                  <div className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}><span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse"></span> <span className="text-gray-500 text-[9px]">{t('locationDetails.lblLocation', 'LOCATION')}</span></div>
                  <div className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}><span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span> <span className="text-gray-500 text-[9px]">{t('locationDetails.tabAssets', 'ASSETS')}</span></div>
                </div>
              </div>
            <MapView 
              lat={location.lat} 
              lng={location.lng} 
              locationId={location.location_id} 
              bim_id={location.bim_id}       
              status={location.status} 
              childAssets={location.assets} // 👈 إرسال الأصول للخريطة      
              t={t}             
              isRTL={isRTL}     
              onLocationChange={(newLat, newLng) => {
                setLocation(prev => ({ ...prev, lat: newLat, lng: newLng })); 
              }}
              onOpen3D={(bimId) => {      
                setActiveTab('3D View');  
                setActiveBimGuid(bimId);  
              }}
              onOpenAsset3D={(assetId) => {
                 // لو عايزينه يروح لشاشة الأصل اللي فيه الـ 3D
                 navigate(`/assets/${assetId}`);
              }}
            />
            <div className={`absolute top-20 ${isRTL ? 'left-4' : 'right-4'} z-[1000] bg-white/90 backdrop-blur-sm p-3 rounded shadow-lg border-l-4 border-blue-600`}>
               <p className="text-[9px] font-bold text-gray-500 uppercase">{t('assets.map.gps_coordinates', 'GPS Coordinates')}</p>
               <code className="text-blue-900 font-black text-xs">{location.lat?.toFixed(5)}, {location.lng?.toFixed(5)}</code>
            </div>
          </div>
          )}

          {/* TAB: 3D View */}
          {activeTab === '3D View' && (
            <div className="bg-slate-900 h-[650px] rounded-xl flex flex-col relative shadow-2xl border-4 border-slate-800 animate-in zoom-in-95 duration-500">
              <div className={`absolute top-6 z-10 flex flex-col gap-3 ${isRTL ? 'right-6 items-end' : 'left-6 items-start'}`}>
                <div className={`bg-white/10 backdrop-blur-md p-3 rounded-lg border border-white/20 flex gap-4 shadow-xl ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Globe className="text-blue-400" size={18} />
                  <div className={`text-[10px] font-bold text-white uppercase ${isRTL ? 'text-right pl-4' : 'pr-4'}`}>
                    <p className="opacity-50">{t('locationDetails.titleModelStatus', 'BIM STATUS')}</p>
                    <p className={location.model_urn ? "text-green-400" : "text-yellow-400"}>
                      {location.model_urn ? t('locationDetails.statusLinked', 'LINKED') : t('locationDetails.statusNotLinked', 'NOT LINKED')}
                    </p>
                  </div>
                  <div className={`border-white/10 flex items-center ${isRTL ? 'border-r pr-4' : 'border-l pl-4'}`}>
                    <label className={`flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded cursor-pointer transition-all ${isRTL ? 'flex-row-reverse' : ''}`}>
                      {isUploading ? <RefreshCw size={14} className="animate-spin" /> : <Upload size={14} />}
                      <span className="font-bold text-[9px] uppercase tracking-tighter">{t('locationDetails.btnUploadRevit', 'UPLOAD REVIT')}</span>
                      <input type="file" accept=".rvt" onChange={handleRevitUpload} className="hidden" />
                    </label>
                  </div>
                </div>
                {location.model_urn && (
                  <div className={`bg-black/40 backdrop-blur-sm p-2 rounded border border-white/5 text-[8px] text-white/50 font-mono break-all w-64 ${isRTL ? 'text-right' : 'text-left'}`}>
                    URN: {location.model_urn}
                  </div>
                )}
              </div>
              <div className="flex-1 w-full h-full p-2">
                 <BIMViewer urn={location.model_urn} activeGuid={activeBimGuid} locationData={location} t={t} />
              </div>
            </div>
          )}

          {/* TAB: History */}
          {activeTab === 'History' && (
             <div className="bg-white border rounded shadow-sm p-6 animate-in slide-in-from-left">
                <h3 className={`text-[#005495] font-black uppercase mb-4 flex items-center gap-2 border-b pb-2 tracking-tighter ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                  <History size={16}/> {t('locationDetails.titleHistory', 'HISTORY')}
                </h3>
                <div className="space-y-4">
                  {location.audit_logs?.map((log, i) => (
                    <div key={i} className={`flex gap-4 ${isRTL ? 'border-r-2 border-blue-500 pr-4 flex-row-reverse text-right' : 'border-l-2 border-blue-500 pl-4'} py-1 hover:bg-blue-50 transition-all`}>
                       <div className="flex flex-col text-[9px] text-gray-400 w-24 text-center">
                          <span className="font-bold">{new Date(log.changed_at).toLocaleDateString()}</span>
                          <span>{new Date(log.changed_at).toLocaleTimeString()}</span>
                       </div>
                       <div className="flex-1">
                          <div className="font-bold text-blue-900 uppercase">{log.action}</div>
                          <div className="text-gray-500 italic">{t('locationDetails.lblChangedBy', 'BY')} {log.changed_by}</div>
                       </div>
                    </div>
                  ))}
                </div>
             </div>
          )}

        </div>
      </div>

      {/* Lookups & Modals */}
      <ClassificationTree isOpen={showClassTree} onClose={() => setShowClassTree(false)} onSelect={handleClassSelect} />
      {lookup.isOpen && (
          <UniversalLookup 
            {...lookup} 
            onClose={() => setLookup({...lookup, isOpen: false})} 
            onSelect={(row) => {
              if (lookup.onCustomSelect) { lookup.onCustomSelect(row); } 
              else {
                const val = row.id || row.location_id || row.value || row.site_id;
                handleInputChange(lookup.field, val);
              }
              setLookup(prev => ({ ...prev, isOpen: false }));
            }} 
          />
      )}
      {showStatusModal && (
        <StatusModal currentStatus={location.status} recordId={location.location_id} appName="LOCATION" domainId={fieldMaps['status']} onClose={() => setShowStatusModal(false)} onComplete={() => window.location.reload()}/>
      )}
      <IncidentModal isOpen={showIncidentModal} onClose={() => setShowIncidentModal(false)} initialData={{ location_id: location.location_id, site_id: location.site_id, org_id: location.org_id }}/>
      {showHistoryModal && (
        <StatusHistoryModal appName="LOCATION" recordId={location.location_id} onClose={() => setShowHistoryModal(false)} />
      )}
      {showAttachments && (
        <AttachmentsModal isOpen={showAttachments} onClose={() => {setShowAttachments(false);fetchCount();}} ownertable="LOCATION" ownerid={location.location_id}/>
      )}
      <EnterLocMeterReadings isOpen={isMeterModalOpen} onClose={() => setIsMeterModalOpen(false)} locationId={location.location_id} meters={location.meters || []} />
      <MeterHistoryModal isOpen={isMeterHistoryOpen} onClose={() => setIsMeterHistoryOpen(false)} locationId={location.location_id} />
      <IncidentHistoryModal isOpen={showIncidentHistory} onClose={() => setShowIncidentHistory(false)} locationId={location.location_id} title={`Incident History for ${location.location_id}`}/>
    </div>
  );
};

// --- المكونات المساعدة المعدلة (UI Kit) ---
const SectionCard = ({ title, children }) => (
  <div className="bg-white border rounded shadow-sm">
    <div className="p-2 bg-slate-50 border-b font-bold text-slate-500 uppercase text-[0.9em] tracking-widest flex items-center gap-2 shadow-inner">
      <div className="w-1 h-3 bg-blue-500 rounded-full"></div> {title}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const HeaderField = ({ label, value, onChange, width = "w-32", isBold, isRequired }) => (
  <div className="flex flex-col">
    <label className={`text-[0.9em] font-bold uppercase tracking-widest leading-none mb-1 ${isRequired ? 'text-red-700' : 'text-gray-400'}`}>
      {label} {isRequired && '*'}
    </label>
    <input 
      className={`${width} border-b border-gray-200 outline-none py-1 font-bold uppercase ${isBold ? 'text-blue-900 text-sm' : 'text-blue-700'} bg-transparent transition-all focus:border-blue-500`} 
      value={value || ''} onChange={e => onChange && onChange(e.target.value)} 
    />
  </div>
);

const SearchField = ({ label, value, onSearch, onChange, isHighlight }) => (
  <div className="flex flex-col border-b border-gray-100 py-1.5 group hover:bg-blue-50/30 transition-all rounded px-1">
    <label className="text-[0.9em] font-bold text-gray-400 uppercase tracking-tighter">{label}</label>
    <div className="flex justify-between items-center h-5">
      <input className={`font-bold uppercase truncate bg-transparent outline-none w-full ${isHighlight ? 'text-green-700 text-sm' : 'text-blue-900'}`} value={value || ''} onChange={(e) => onChange && onChange(e.target.value)}/>
      {onSearch && <Search size={13} className="text-blue-400 cursor-pointer hover:text-blue-800 transition-colors" onClick={onSearch} />}
    </div>
  </div>
);

const DataField = ({ label, value, isHighlight }) => (
  <div className="flex flex-col border-b border-gray-100 py-1">
    <label className="text-[0.9em] font-bold text-gray-400 uppercase tracking-tighter">{label}</label>
    <div className={`font-bold uppercase ${isHighlight ? 'text-green-700 text-sm' : 'text-blue-900'}`}>{value || ''}</div>
  </div>
);

const StaticField = ({ label, value }) => (
  <div className="flex flex-col border-b border-gray-100 py-1 opacity-60">
    <label className="text-[0.9em] font-bold text-gray-400 uppercase tracking-tighter">{label}</label>
    <div className="font-bold text-gray-600 italic">{value || ''}</div>
  </div>
);

const EditableField = ({ label, value, onChange, onSearch, isRequired, hasList, hasCalendar, desc, type = "text" }) => (
  <div className="flex flex-col gap-1 border-b border-gray-300 group">
    <label className={`${isRequired ? 'text-red-800' : 'text-gray-400'} font-bold text-[0.9em] uppercase tracking-tighter`}>{label}</label>
    <div className="flex items-center justify-between pb-1">
      <input type={type} className="outline-none font-bold text-blue-900 bg-transparent w-full custom-datetime-input" value={value || ''} onChange={e => onChange && onChange(e.target.value)} />
      {desc && <span className="text-gray-400 font-normal px-4 italic whitespace-nowrap">{desc}</span>}
      <div className="flex gap-2 text-blue-700 cursor-pointer">
        {hasList && <List size={14}/>}
        {hasCalendar && <Calendar size={14}/>}
        {onSearch && <Search size={13} className="text-blue-400 cursor-pointer hover:text-blue-800 transition-colors" onClick={onSearch} />}
      </div>
    </div>
  </div>
);

export default LocationDetails;